/**
 * Portfolio Explorer data hook — owns Orion query lifecycle, mutable row tree,
 * single-row lazy expansion, bulk "expand to level" / "expand all children"
 * with concurrency pooling, and cancellation support.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useOrionDomainQuery, useOrionIdentifier, postOrionDomainQuery } from '../../../../api/coreData'
import { buildOrionRequest, buildChildRequest, buildFilteredChildRequest } from '../lib/buildOrionRequest'
import { transformResponse, transformChildResponse } from '../lib/transformResponse'
import { ENTITY_SCOPABLE_GROUPINGS } from '../lib/constants'

const LAZY_TIMEOUT = 5 * 60 * 1000

/**
 * Finds a row by _rowId in a row tree (depth-first).
 * Returns the row reference so it can be mutated in place.
 */
function findRow (rows, rowId) {
  for (const row of rows) {
    if (row._rowId === rowId) return row
    if (row.data) {
      const found = findRow(row.data, rowId)
      if (found) return found
    }
  }
  return null
}

/**
 * Collects rows at exactly `targetDepth` whose children haven't been fetched yet.
 * Used by expandToLevelLazy to find the next batch of rows to load at each depth.
 */
function findUnloadedAtDepth (rows, targetDepth, currentDepth = 0) {
  const result = []
  for (const row of rows) {
    if (currentDepth === targetDepth && row._childrenLoaded === false) result.push(row)
    if (row.data && currentDepth < targetDepth) {
      result.push(...findUnloadedAtDepth(row.data, targetDepth, currentDepth + 1))
    }
  }
  return result
}

/**
 * Recursively collects every unloaded row in a subtree.
 * Used by expandAllChildrenLazy to discover all pending nodes under a parent.
 */
function findAllUnloadedInSubtree (rows) {
  const result = []
  for (const row of rows) {
    if (row._childrenLoaded === false) result.push(row)
    if (row.data) result.push(...findAllUnloadedInSubtree(row.data))
  }
  return result
}

/**
 * Runs `fn` over `items` with at most `concurrency` in-flight promises.
 * Workers pull from a shared index; `shouldCancel` is checked between items.
 */
async function asyncPool (items, fn, concurrency, shouldCancel) {
  let i = 0
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (i < items.length && !shouldCancel()) {
      const item = items[i++]
      await fn(item)
    }
  })
  await Promise.all(workers)
}

/**
 * Fetches child rows for a single expandable node and attaches them in place.
 * Handles both entity-scopable groupings (direct child request) and
 * non-scopable groupings (filtered child request using ancestor context).
 */
async function fetchRowChildren (target, { config, clientId, asOfDate, globalDateConfig, entity, entityIds, totalRow }) {
  const groupingIndex = config.groupings.indexOf(target.grouping)
  const childDepthIndex = groupingIndex >= 0 ? groupingIndex + 1 : 1
  const remainingGroupingCount = config.groupings.length - childDepthIndex
  const isScopable = ENTITY_SCOPABLE_GROUPINGS.has(target.grouping)

  const childMaxDepth = 1
  const childHasMoreLevels = remainingGroupingCount > 1

  let childRequestBody
  if (isScopable) {
    childRequestBody = buildChildRequest(config, {
      asOfDate,
      depthIndex: childDepthIndex,
      maxGroupingDepth: childMaxDepth,
      entity: target.grouping,
      entityIds: [target.groupEntityId]
    })
  } else {
    const ancestorEnt = target._ancestorEntity || entity
    const ancestorIds = target._ancestorEntityIds || entityIds
    childRequestBody = buildFilteredChildRequest(config, {
      asOfDate,
      depthIndex: childDepthIndex,
      maxGroupingDepth: childMaxDepth,
      entity: ancestorEnt,
      entityIds: ancestorIds,
      filterGrouping: target.grouping,
      filterName: target.name
    })
  }

  const childResponse = await postOrionDomainQuery('reporting/scope', clientId, childRequestBody)

  const scopeEntity = isScopable ? target.grouping : (target._ancestorEntity || entity)
  const scopeEntityIds = isScopable ? [target.groupEntityId] : (target._ancestorEntityIds || entityIds)

  let childRows = transformChildResponse(childResponse, {
    hasMoreLevels: childHasMoreLevels,
    columnInstances: config.columns,
    globalDateConfig,
    entity: scopeEntity,
    entityIds: scopeEntityIds,
    parentRow: target,
    totalRow
  })

  if (!isScopable) {
    const match = childRows.find(r =>
      (target.groupEntityId != null && r.groupEntityId === target.groupEntityId) ||
      r.name === target.name
    )
    if (match?.data) {
      childRows = match.data
    }
  }

  target.data = childRows.length > 0 ? childRows : undefined
  target._childrenLoaded = true
}

export function usePortfolioExplorerQuery (config, asOfDate) {
  const { clientId } = useAppContext()
  const isLazy = !config.fetchAllLevels && config.groupings.length > 1

  const { data: orionIdentity } = useOrionIdentifier(clientId)
  const entity = orionIdentity?.entity
  const entityIds = orionIdentity?.entityIds

  const globalDateConfig = useMemo(() => ({
    dateMode: config.dateMode,
    quickDate: config.quickDate,
    startDate: config.startDate,
    endDate: config.endDate
  }), [config.dateMode, config.quickDate, config.startDate, config.endDate])

  const request = useMemo(() => {
    if (!asOfDate || !entity || !entityIds || config.columns.length === 0) return null
    return buildOrionRequest(config, {
      asOfDate,
      maxGroupingDepth: isLazy ? 1 : undefined,
      entity,
      entityIds
    })
  }, [config, asOfDate, isLazy, entity, entityIds])

  const { data: rawData, isFetching, error, refetch } = useOrionDomainQuery(
    'reporting/scope',
    clientId,
    request,
    { enabled: false }
  )

  const hasMoreLevels = isLazy && config.groupings.length > 1

  const { baseRows, totalRow } = useMemo(() => {
    if (!rawData) return { baseRows: [], totalRow: null }
    const result = transformResponse(rawData, {
      hasMoreLevels,
      columnInstances: config.columns,
      globalDateConfig,
      entity,
      entityIds
    })
    return { baseRows: result.rows, totalRow: result.totalRow }
  }, [rawData, hasMoreLevels, config.columns, globalDateConfig, entity, entityIds])

  const totalRowRef = useRef(null)
  totalRowRef.current = totalRow

  const [rowTree, setRowTree] = useState([])
  const rowTreeRef = useRef([])
  const lastBaseRowsRef = useRef(null)

  // Intentional setState-during-render to sync mutable row tree with derived baseRows.
  // This avoids stale-closure issues that useEffect would introduce for the ref+state
  // pairing needed by expandRow. React batches the extra render. Revisit if warnings appear.
  if (baseRows !== lastBaseRowsRef.current) {
    lastBaseRowsRef.current = baseRows
    const cloned = JSON.parse(JSON.stringify(baseRows))
    rowTreeRef.current = cloned
    setRowTree(cloned)
  }

  const [lazyExpandState, setLazyExpandState] = useState(null)
  const lazyCancelRef = useRef(false)

  const fetchCtx = useMemo(() => ({
    config,
    clientId,
    asOfDate,
    globalDateConfig,
    entity,
    entityIds
  }), [config, clientId, asOfDate, globalDateConfig, entity, entityIds])

  /** Fetches and attaches children for a single row on demand (one-level expand). */
  const expandRow = useCallback(async (row) => {
    if (!clientId || !asOfDate || row._childrenLoaded) return

    const target = findRow(rowTreeRef.current, row._rowId)
    if (!target) return

    target._subRowsFetching = true
    setRowTree([...rowTreeRef.current])

    try {
      await fetchRowChildren(target, { ...fetchCtx, totalRow: totalRowRef.current })
    } catch (err) {
      console.error('Failed to expand row', err)
      target.data = undefined
      target._childrenLoaded = true
    } finally {
      target._subRowsFetching = false
      setRowTree([...rowTreeRef.current])
    }
  }, [clientId, asOfDate, fetchCtx])

  /**
   * Bulk-expands the tree to `targetDepth`, fetching one depth-layer at a time
   * with concurrency pooling. Reports progress via lazyExpandState and respects
   * cancellation (lazyCancelRef) and a 5-minute timeout.
   */
  const expandToLevelLazy = useCallback(async (targetDepth, groupings) => {
    lazyCancelRef.current = false
    const startTime = Date.now()
    const progress = { completedRows: 0, totalRows: 0 }

    setLazyExpandState({
      targetLevelName: groupings[targetDepth] || groupings[groupings.length - 1],
      currentLevel: 0,
      totalLevels: targetDepth,
      completedRows: 0,
      totalRows: 0
    })

    let cancelled = false
    let timedOut = false

    for (let depth = 0; depth < targetDepth; depth++) {
      if (lazyCancelRef.current) { cancelled = true; break }
      if (Date.now() - startTime > LAZY_TIMEOUT) { timedOut = true; break }

      const unloaded = findUnloadedAtDepth(rowTreeRef.current, depth)
      if (unloaded.length === 0) {
        setLazyExpandState(prev => prev && ({ ...prev, currentLevel: depth + 1 }))
        continue
      }

      progress.totalRows += unloaded.length
      setLazyExpandState(prev => prev && ({
        ...prev,
        currentLevel: depth + 1,
        totalRows: progress.totalRows,
        completedRows: progress.completedRows
      }))

      const ctx = { ...fetchCtx, totalRow: totalRowRef.current }

      await asyncPool(unloaded, async (row) => {
        try {
          await fetchRowChildren(row, ctx)
        } catch (err) {
          console.error('Lazy expand: failed to fetch row', row.name, err)
          row.data = undefined
          row._childrenLoaded = true
        }
        progress.completedRows++
        setLazyExpandState(prev => prev && ({ ...prev, completedRows: progress.completedRows }))
      }, 3, () => lazyCancelRef.current || Date.now() - startTime > LAZY_TIMEOUT)

      setRowTree([...rowTreeRef.current])

      if (lazyCancelRef.current) { cancelled = true; break }
      if (Date.now() - startTime > LAZY_TIMEOUT) { timedOut = true; break }
    }

    setLazyExpandState(null)
    return { cancelled, timedOut, targetDepth }
  }, [fetchCtx])

  /**
   * Recursively expands every descendant under `row`, fetching in repeated
   * passes until no unloaded nodes remain. Uses the same concurrency pool,
   * progress reporting, cancellation, and timeout as expandToLevelLazy.
   */
  const expandAllChildrenLazy = useCallback(async (row) => {
    const target = findRow(rowTreeRef.current, row._rowId)
    if (!target) return { cancelled: false, timedOut: false }

    lazyCancelRef.current = false
    const startTime = Date.now()
    const progress = { completedRows: 0, totalRows: 0, pass: 0 }

    setLazyExpandState({
      targetLevelName: row.name,
      currentLevel: 0,
      totalLevels: 0,
      completedRows: 0,
      totalRows: 0
    })

    let cancelled = false
    let timedOut = false

    while (true) {
      if (lazyCancelRef.current) { cancelled = true; break }
      if (Date.now() - startTime > LAZY_TIMEOUT) { timedOut = true; break }

      const unloaded = findAllUnloadedInSubtree([target])
      if (unloaded.length === 0) break

      progress.pass++
      progress.totalRows += unloaded.length
      setLazyExpandState(prev => prev && ({
        ...prev,
        currentLevel: progress.pass,
        totalRows: progress.totalRows,
        completedRows: progress.completedRows
      }))

      const ctx = { ...fetchCtx, totalRow: totalRowRef.current }

      await asyncPool(unloaded, async (r) => {
        try {
          await fetchRowChildren(r, ctx)
        } catch (err) {
          console.error('Lazy expand children: failed to fetch row', r.name, err)
          r.data = undefined
          r._childrenLoaded = true
        }
        progress.completedRows++
        setLazyExpandState(prev => prev && ({ ...prev, completedRows: progress.completedRows }))
      }, 3, () => lazyCancelRef.current || Date.now() - startTime > LAZY_TIMEOUT)

      setRowTree([...rowTreeRef.current])

      if (lazyCancelRef.current) { cancelled = true; break }
      if (Date.now() - startTime > LAZY_TIMEOUT) { timedOut = true; break }
    }

    setLazyExpandState(null)
    return { cancelled, timedOut }
  }, [fetchCtx])

  const cancelLazyExpand = useCallback(() => {
    lazyCancelRef.current = true
  }, [])

  return {
    rows: rowTree,
    totalRow,
    isFetching,
    error,
    refetch,
    expandRow,
    expandToLevelLazy,
    expandAllChildrenLazy,
    lazyExpandState,
    cancelLazyExpand,
    hasClient: !!clientId,
    isReady: request !== null
  }
}
